import { motion, useReducedMotion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three";

import earthDayMapUrl from "../assets/earth-day.jpg";

const REGIONS = [
  { id: "in", name: "India", detail: "South Asia", lat: 22, lon: 79 },
  { id: "ke", name: "Kenya", detail: "East Africa", lat: 0.5, lon: 37.5 },
  { id: "eg", name: "Egypt", detail: "North Africa", lat: 26.5, lon: 30 },
  { id: "rw", name: "Rwanda", detail: "East Africa", lat: -1.94, lon: 29.87 },
];

/** Unit direction on the globe matching marker placement (phi/theta on Y-up sphere). */
function latLonToDirection(latDeg, lonDeg, target = new THREE.Vector3()) {
  const phi = ((90 - latDeg) * Math.PI) / 180;
  const theta = ((lonDeg + 180) * Math.PI) / 180;
  const x = -Math.sin(phi) * Math.cos(theta);
  const y = Math.cos(phi);
  const z = Math.sin(phi) * Math.sin(theta);
  return target.set(x, y, z).normalize();
}

const PRIMARY_MARKER_HEX = 0xffffff;
/** `primary.muted` — visible on Earth texture, on-brand for non-selected pins */
const INACTIVE_MARKER_HEX = 0x9a8700;

/** Rad/s at full speed — one full turn ≈ 24s */
const AUTO_ROTATE_SPEED = (Math.PI * 2) / 24;
/** When prefers-reduced-motion is on, spin slower but still visibly (≈ 3–4 min per turn) */
const REDUCED_MOTION_ROTATE_FACTOR = 0.12;
const MARKER_POP_SCALE = 1.42;
const MARKER_SCALE_LERP = 0.14;
/** Marker "beacon ping" cadence (rad/s) and amplitudes */
const MARKER_PING_OMEGA = 4.1;
const MARKER_PING_AMP_ACTIVE = 0.2;
const MARKER_PING_AMP_IDLE = 0.06;
const MARKER_PING_OMEGA_REDUCED = 2.1;
const MARKER_PING_AMP_ACTIVE_REDUCED = 0.08;
const MARKER_PING_AMP_IDLE_REDUCED = 0.03;
/** Camera looks from +Z toward origin; front-facing pins maximize world Z. */
const FACING_AXIS = new THREE.Vector3(0, 0, 1);

export default function DeploymentFootprint() {
  const reduceMotion = useReducedMotion();
  const mountRef = useRef(null);
  const resizeGlobeRef = useRef(null);
  const frameRef = useRef(0);
  const globeGroupRef = useRef(null);
  const markerMaterialsRef = useRef(new Map());
  const scratchDirRef = useRef(new THREE.Vector3());
  const lastHighlightIdRef = useRef(null);
  const [activeId, setActiveId] = useState(REGIONS[0].id);
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;

  const syncGlobeMarkerColors = useCallback((selectedId) => {
    markerMaterialsRef.current.forEach((mat, id) => {
      mat.color.setHex(id === selectedId ? PRIMARY_MARKER_HEX : INACTIVE_MARKER_HEX);
    });
  }, []);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const markerMatById = markerMaterialsRef.current;
    const markerMeshById = new Map();
    const regionLocalDirs = REGIONS.map((r) =>
      latLonToDirection(r.lat, r.lon, new THREE.Vector3())
    );

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    camera.position.z = 2.85;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;

    const globeGroup = new THREE.Group();
    globeGroupRef.current = globeGroup;
    scene.add(globeGroup);

    const sphereGeom = new THREE.SphereGeometry(1, 56, 40);

    const globeMat = new THREE.MeshBasicMaterial({
      color: 0x2d2600,
    });
    const globe = new THREE.Mesh(sphereGeom, globeMat);
    globeGroup.add(globe);

    let textureLoadCancelled = false;
    new THREE.TextureLoader().load(
      earthDayMapUrl,
      (tex) => {
        if (textureLoadCancelled) {
          tex.dispose();
          return;
        }
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
        globeMat.map = tex;
        globeMat.color.setHex(0xffffff);
        globeMat.needsUpdate = true;
      },
      undefined,
      () => {
        /* keep solid ocean color if texture missing or blocked */
      }
    );

    markerMatById.clear();
    REGIONS.forEach((region, regionIndex) => {
      const { x, y, z } = latLonToDirection(region.lat, region.lon, new THREE.Vector3());
      const markerGeom = new THREE.SphereGeometry(0.035, 12, 12);
      const markerMat = new THREE.MeshBasicMaterial({ color: INACTIVE_MARKER_HEX });
      markerMatById.set(region.id, markerMat);
      const marker = new THREE.Mesh(markerGeom, markerMat);
      marker.position.set(x, y, z);
      marker.userData.baseScale = 1;
      marker.userData.pulsePhase = regionIndex * 1.37;
      markerMeshById.set(region.id, marker);
      globeGroup.add(marker);
    });

    globeGroup.quaternion.identity();

    const syncCanvasCss = (cssW, cssH) => {
      const canvas = renderer.domElement;
      canvas.style.display = "block";
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
    };

    const setSize = () => {
      const canvas = renderer.domElement;
      const br = canvas.getBoundingClientRect();
      let rw = br.width;
      let rh = br.height;
      if (rw < 1 || rh < 1) {
        rw = mount.clientWidth;
        rh = mount.clientHeight;
      }
      if (rw < 1 || rh < 1) return;

      const w = Math.max(1, Math.round(rw));
      const h = Math.max(1, Math.round(rh));
      const aspect = w / h;

      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(w, h, false);

      camera.aspect = aspect;
      camera.updateProjectionMatrix();

      syncCanvasCss(w, h);
    };

    resizeGlobeRef.current = setSize;

    mount.appendChild(renderer.domElement);
    syncCanvasCss(
      Math.max(1, mount.clientWidth),
      Math.max(1, mount.clientHeight)
    );
    setSize();
    requestAnimationFrame(() => {
      setSize();
      requestAnimationFrame(setSize);
    });

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(setSize);
    });
    ro.observe(mount);
    ro.observe(renderer.domElement);

    const clock = new THREE.Clock();
    const worldUp = new THREE.Vector3(0, 1, 0);
    const rendererSizeScratch = new THREE.Vector2();

    const tick = () => {
      frameRef.current = requestAnimationFrame(tick);
      const g = globeGroupRef.current;
      if (!g) return;

      const canvas = renderer.domElement;
      const cw = canvas.clientWidth;
      const ch = canvas.clientHeight;
      if (cw >= 1 && ch >= 1) {
        renderer.getSize(rendererSizeScratch);
        const bufW = rendererSizeScratch.x;
        const bufH = rendererSizeScratch.y;
        if (Math.abs(bufW - cw) > 0.75 || Math.abs(bufH - ch) > 0.75) {
          setSize();
        } else {
          const nextAspect = cw / ch;
          if (Math.abs(camera.aspect - nextAspect) > 1e-5) {
            camera.aspect = nextAspect;
            camera.updateProjectionMatrix();
          }
        }
      }

      // Always rotate: gating on useReducedMotion() alone often disables the globe entirely
      // because many users have "prefers-reduced-motion" enabled at the OS level.
      const dt = Math.min(Math.max(clock.getDelta(), 1 / 240), 0.05);
      const speedMul = reduceMotion ? REDUCED_MOTION_ROTATE_FACTOR : 1;
      g.rotateOnWorldAxis(worldUp, AUTO_ROTATE_SPEED * speedMul * dt);

      let bestId = REGIONS[0].id;
      let bestDot = -Infinity;
      const scratch = scratchDirRef.current;
      for (let i = 0; i < REGIONS.length; i++) {
        scratch.copy(regionLocalDirs[i]).applyQuaternion(g.quaternion);
        const dot = scratch.dot(FACING_AXIS);
        if (dot > bestDot) {
          bestDot = dot;
          bestId = REGIONS[i].id;
        }
      }

      if (bestId !== lastHighlightIdRef.current) {
        lastHighlightIdRef.current = bestId;
        syncGlobeMarkerColors(bestId);
        if (bestId !== activeIdRef.current) {
          activeIdRef.current = bestId;
          setActiveId(bestId);
        }
      }

      const elapsed = clock.getElapsedTime();
      const pingOmega = reduceMotion ? MARKER_PING_OMEGA_REDUCED : MARKER_PING_OMEGA;
      const pingAmpActive = reduceMotion ? MARKER_PING_AMP_ACTIVE_REDUCED : MARKER_PING_AMP_ACTIVE;
      const pingAmpIdle = reduceMotion ? MARKER_PING_AMP_IDLE_REDUCED : MARKER_PING_AMP_IDLE;

      markerMeshById.forEach((mesh, id) => {
        const baseTarget = id === bestId ? MARKER_POP_SCALE : 1;
        mesh.userData.baseScale = THREE.MathUtils.lerp(
          mesh.userData.baseScale,
          baseTarget,
          MARKER_SCALE_LERP
        );
        const amp = id === bestId ? pingAmpActive : pingAmpIdle;
        const phase = mesh.userData.pulsePhase ?? 0;
        const t = elapsed * pingOmega + phase;

        // Beacon-style animation: fast ping up, then smooth settle.
        const pingWave = Math.max(0, Math.sin(t));
        const pingShape = pingWave ** 2.4;
        const ping = 1 + amp * pingShape;

        mesh.scale.setScalar(mesh.userData.baseScale * ping);
      });

      renderer.render(scene, camera);
    };
    tick();

    lastHighlightIdRef.current = null;
    syncGlobeMarkerColors(activeIdRef.current);

    return () => {
      resizeGlobeRef.current = null;
      textureLoadCancelled = true;
      markerMatById.clear();
      lastHighlightIdRef.current = null;
      cancelAnimationFrame(frameRef.current);
      ro.disconnect();
      globeGroup.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry?.dispose();
          const mat = obj.material;
          const mats = Array.isArray(mat) ? mat : [mat];
          mats.forEach((m) => {
            m.map?.dispose();
            m.dispose();
          });
        }
      });
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
      globeGroupRef.current = null;
    };
  }, [reduceMotion, syncGlobeMarkerColors]);

  return (
    <div className="mt-12 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm shadow-slate-900/5 md:mt-16">
      {/* Editorial header — full width */}
      <header className="border-b border-slate-100 px-6 py-8 md:px-10 md:py-10">
        <motion.div
          className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between lg:gap-10"
          animate={
            reduceMotion
              ? undefined
              : {
                  scale: [1, 1.01, 1],
                }
          }
          transition={
            reduceMotion
              ? undefined
              : {
                  duration: 2.8,
                  ease: "easeInOut",
                  repeat: Infinity,
                }
          }
        >
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              Deployment footprint
            </p>
            <h3 className="font-display mt-3 text-2xl font-bold text-secondary md:text-3xl lg:text-[1.75rem] lg:leading-tight xl:text-3xl">
              Deployed Across Diverse Healthcare Environments
            </h3>
            <p className="mt-3 text-secondary/75 md:text-[1.05rem] md:leading-relaxed">
              Our solutions are trusted by hospitals and healthcare providers across
              multiple countries and regions. The globe rotates through each region as
              it comes into view.
            </p>
          </div>
          <motion.p
            className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-center text-xs font-semibold tabular-nums text-secondary/80 lg:text-left"
            animate={
              reduceMotion
                ? undefined
                : {
                    scale: [1, 1.06, 1],
                    boxShadow: [
                      "0 0 0 0 rgba(255, 221, 0, 0.22)",
                      "0 0 0 8px rgba(255, 221, 0, 0)",
                      "0 0 0 0 rgba(255, 221, 0, 0)",
                    ],
                  }
            }
            transition={
              reduceMotion
                ? undefined
                : {
                    duration: 2,
                    ease: "easeInOut",
                    repeat: Infinity,
                  }
            }
          >
            <span className="text-secondary">{REGIONS.length}</span>
            <span className="font-normal text-secondary/60"> active regions</span>
          </motion.p>
        </motion.div>
      </header>

      {/* Split body: regions (rail) + globe (stage) */}
      <div className="grid lg:grid-cols-12">
        <div
          className="border-slate-100 bg-white px-6 py-8 md:px-8 md:py-10 lg:col-span-5 lg:border-r xl:col-span-4"
          role="list"
          aria-label="Deployment regions"
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
            Where we deploy
          </p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
            {REGIONS.map((r) => {
              const isActive = r.id === activeId;
              return (
                <div
                  key={r.id}
                  role="listitem"
                  aria-current={isActive ? "true" : undefined}
                  className={`relative flex w-full overflow-hidden rounded-xl border p-4 text-left transition-[box-shadow,background-color,border-color] duration-300 ${
                    isActive
                      ? "border-primary/50 bg-primary/20 shadow-sm ring-1 ring-primary/35"
                      : "border-slate-200 bg-white/80 shadow-sm shadow-slate-900/[0.03]"
                  }`}
                >
                  <div className="relative z-10 flex w-full min-w-0 gap-3">
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl transition-colors duration-300"
                    >
                      <img
                        src={`https://flagcdn.com/w80/${r.id}.png`}
                        alt=""
                        aria-hidden
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-secondary">{r.name}</p>
                      <p className="text-sm text-secondary/70">{r.detail}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative min-h-[320px] lg:col-span-7 lg:min-h-[460px] xl:col-span-8">
          <div
            className="pointer-events-none absolute bottom-10 left-1/2 h-16 w-[58%] -translate-x-1/2 rounded-full bg-slate-900/18 blur-2xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_85%_70%_at_50%_45%,rgba(255,221,0,0.08)_0%,transparent_55%)]"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-100/40 via-transparent to-transparent"
            aria-hidden
          />
          <div
            ref={mountRef}
            className="pointer-events-none absolute inset-0 touch-none"
            role="img"
            aria-label={`Rotating globe; ${REGIONS.find((x) => x.id === activeId)?.name ?? "deployment regions"} is centered in view`}
          />
          <p className="pointer-events-none absolute bottom-4 left-4 right-4 text-center text-[11px] text-secondary/55 md:left-8 md:right-8">
            <span className="inline-block rounded-full bg-white/90 px-3 py-1.5 shadow-sm ring-1 ring-slate-200/80 backdrop-blur-sm">
              Our Global Deployment Regions
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
